import React, { useCallback, useContext, useRef } from 'react';
import BasicIcon from '../../../../BasicIcon/BasicIcon';
import { ThemeContext } from '../../../../ThemeProvider/ThemeProvider';
import {
  IconContainer,
  SibebarNodeContent,
  SibebarNodeLabel,
  SidebarNodeBox,
  SidebarNodeBoxLink,
  SidebarNodeContainer,
} from './styles';
import { SideBarNodeProps } from './types';

export const SideBarNode: React.FC<SideBarNodeProps> = ({
  testId,
  label,
  iconName,
  opened = false,
  action,
  children,
  onClose,
  path,
  selectNode,
  toggleNode,
  history,
  keepOpen = false,
  isBarOpen,
}) => {
  const { actualTheme } = useContext(ThemeContext);
  const itemContainerRef = useRef(null);
  const active = history?.location?.pathname.startsWith(path);

  const onClick = useCallback(() => {
    if (!path && toggleNode) toggleNode();
    if (path) {
      if (onClose) onClose();
    }
    if (action) {
      action();
    } else if (path && history) {
      history.push(path);
    }
  }, [toggleNode, path, action, history, onClose]);

  const onLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  const sideBarNodeBoxContent = () => (
    <>
      <BasicIcon
        name={iconName}
        color={
          (path || action) && active
            ? actualTheme.sidebar.node.active
            : actualTheme.sidebar.node.color
        }
        clickable
      />
      <SibebarNodeLabel opened={opened} active={(path || action) && active}>
        {label}
      </SibebarNodeLabel>
      {children && !path && !action && !keepOpen && (
        <IconContainer isOpen={opened}>
          <BasicIcon name="chevron-down" color={actualTheme.sidebar.node.color} />
        </IconContainer>
      )}
    </>
  );

  return (
    <SidebarNodeContainer data-testid={testId}>
      {path ? (
        <SidebarNodeBoxLink onClick={onLinkClick} href={path}>
          {sideBarNodeBoxContent()}
        </SidebarNodeBoxLink>
      ) : (
        <SidebarNodeBox cursorPointer={!keepOpen && !!children} onClick={!keepOpen && onClick}>
          {sideBarNodeBoxContent()}
        </SidebarNodeBox>
      )}
      {children && !path && !action && (
        <SibebarNodeContent
          maxHeight={itemContainerRef.current?.offsetHeight}
          opened={opened || keepOpen}
        >
          <div ref={itemContainerRef}>
            {React.Children.map(children, (child) => {
              // Checking isValidElement is the safe way and avoids a typescript
              // error too.
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  onClose,
                  selectNode,
                  history,
                  opened,
                  isBarOpen,
                });
              }
              return child;
            })}
          </div>
        </SibebarNodeContent>
      )}
    </SidebarNodeContainer>
  );
};

export default SideBarNode;
